import { useState, useEffect, useMemo, useRef } from 'react'
import {
   //@ts-ignore
   formatFloatAsCurrencySymbol,
   //@ts-ignore
   formatFloatAsPercent,
   //@ts-ignore
   removeDiscountNameVariables,
} from '../../common/helpers.mjs'
import { useQuoteContext } from '../../hooks/useQuoteContext'
import {
   shouldDisableDiscount,
   shouldDisableDiscountFamilyOption,
} from '../../js/validate'

interface DiscountFamilyDropdownProps {
   discountFamily: string
   order?: number
}

interface DiscountFamilyDiscountOptionProps {
   discountKey: string
   isSelected: boolean
   onSelect: (discountKey: string) => void
   disabled?: boolean
   disabledReason?: string | null
}

// Builds a human-readable reason a row is disabled, based on which term/prepay
// constraint isn't satisfied. Returns null if no constraint reason applies.
function getDisabledReason(
   state: ReturnType<typeof useQuoteContext>,
   discountKey: string,
): string | null {
   const discount = state.discount_list?.[discountKey]
   if (!discount) return null
   const termLength = state.termLength || 1
   const prepayLength = state.prepayLength || 1
   const reasons: string[] = []
   if (discount.TermLengthMin != null && termLength < discount.TermLengthMin) {
      reasons.push(
         `Requires a term length of at least ${discount.TermLengthMin} months (currently ${termLength}).`,
      )
   }
   if (discount.TermLengthMax != null && termLength > discount.TermLengthMax) {
      reasons.push(
         `Requires a term length of at most ${discount.TermLengthMax} months (currently ${termLength}).`,
      )
   }
   if (
      discount.PrepayLengthMin != null &&
      prepayLength < discount.PrepayLengthMin
   ) {
      reasons.push(
         `Requires an upfront prepayment of at least ${discount.PrepayLengthMin} months (currently ${prepayLength}).`,
      )
   }
   if (
      discount.PrepayLengthMax != null &&
      prepayLength > discount.PrepayLengthMax
   ) {
      reasons.push(
         `Requires no upfront prepayment beyond ${discount.PrepayLengthMax} month${discount.PrepayLengthMax === 1 ? '' : 's'} (currently ${prepayLength}).`,
      )
   }
   return reasons.length > 0 ? reasons.join(' ') : null
}

export function DiscountFamilyDropdown({
   discountFamily,
   order = 99,
}: DiscountFamilyDropdownProps) {
   const state = useQuoteContext()
   const [isOpen, setIsOpen] = useState(false)
   const [selectedDiscountKey, setSelectedDiscountKey] = useState<
      string | null
   >(null)
   const dropdownRef = useRef<HTMLDivElement>(null)

   // Local string state preserves trailing zeros while the user is typing the number discount input
   const storedNumericValue: number =
      (selectedDiscountKey &&
         state.promotionalDiscounts?.numberDiscountValues?.[
            selectedDiscountKey
         ]) ??
      0
   const [displayValue, setDisplayValue] = useState<string>(
      storedNumericValue > 0 ? storedNumericValue.toString() : '',
   )

   // Reset display value when the selected discount changes or external state updates
   useEffect(() => {
      setDisplayValue(
         storedNumericValue > 0 ? storedNumericValue.toString() : '',
      )
   }, [selectedDiscountKey, storedNumericValue])

   // Sync boolean activation flag for the selected discount after render.
   // Previously these setPromotionalDiscounts calls lived inside renderDiscountInput(),
   // which caused races: firing on every render meant multiple family members could
   // each re-set their own flag in the same render cycle without clearing the others.
   useEffect(() => {
      if (!selectedDiscountKey) return
      const discount = state.discount_list[selectedDiscountKey]
      if (!discount) return

      const isDisabled = shouldDisableDiscount(state, selectedDiscountKey)

      if (discount.InputType !== 'Number') {
         // Boolean discount: ensure flag is true
         if (!state.promotionalDiscounts[selectedDiscountKey]) {
            state.setPromotionalDiscounts((prev) => ({
               ...prev,
               [selectedDiscountKey]: true,
            }))
         }
      } else if (isDisabled) {
         // Disabled number discount: activate flag if a saved value exists
         const savedValue =
            state.promotionalDiscounts?.numberDiscountValues?.[
               selectedDiscountKey
            ]
         if (
            savedValue != null &&
            savedValue > 0 &&
            !state.promotionalDiscounts[selectedDiscountKey]
         ) {
            state.setPromotionalDiscounts((prev) => ({
               ...prev,
               [selectedDiscountKey]: true,
            }))
         }
      }
   }, [
      selectedDiscountKey,
      state.discount_list,
      // Only re-run when the selected discount's own flag or saved number value
      // changes — listing the whole promotionalDiscounts object re-runs the effect
      // on unrelated changes.
      selectedDiscountKey
         ? state.promotionalDiscounts[selectedDiscountKey]
         : undefined,
      selectedDiscountKey
         ? state.promotionalDiscounts?.numberDiscountValues?.[
              selectedDiscountKey
           ]
         : undefined,
   ])

   // Handle clicks outside the dropdown
   useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
         if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
         ) {
            setIsOpen(false)
         }
      }

      // Add event listener when dropdown is open
      if (isOpen) {
         document.addEventListener('mousedown', handleClickOutside)
      }

      // Clean up the event listener
      return () => {
         document.removeEventListener('mousedown', handleClickOutside)
      }
   }, [isOpen])

   // Get all discounts that belong to this family.
   // Role filter: hide rows whose RoleRequired excludes the active user_role,
   // unless the row is currently saved on this quote (preserve so role
   // escalation/demotion never blanks out a saved selection).
   const familyDiscounts = useMemo(() => {
      const discounts: string[] = []
      const savedCode = state.promotionalDiscounts?.[discountFamily]
      for (const discountKey in state.discount_list) {
         const row = state.discount_list[discountKey]
         if (row?.DiscountFamily !== discountFamily) continue
         const roleRequired: string | undefined | null = row?.RoleRequired
         const isSaved = savedCode === row?.DiscountCode
         const hasRole =
            !roleRequired ||
            state.user_role === 'Super Admin' ||
            roleRequired
               .split(',')
               .map((r: string) => r.trim())
               .includes(state.user_role)
         if (hasRole || isSaved) {
            discounts.push(discountKey)
         }
      }
      return discounts.sort((a, b) => {
         const orderA = state.discount_list[a]?.DisplayOrder || 999
         const orderB = state.discount_list[b]?.DisplayOrder || 999
         return orderA - orderB
      })
   }, [
      state.discount_list,
      discountFamily,
      state.user_role,
      state.promotionalDiscounts,
   ])

   // Check if any discount from this family is currently selected
   useEffect(() => {
      const currentlySelected = familyDiscounts.find(
         (discountKey) =>
            state.promotionalDiscounts[discountFamily] ===
            state.discount_list[discountKey]?.DiscountCode,
      )
      setSelectedDiscountKey(currentlySelected || null)
   }, [
      state.promotionalDiscounts,
      discountFamily,
      familyDiscounts,
      state.discount_list,
   ])

   // Auto-deselect discount when term/prepay length constraints are no longer met
   useEffect(() => {
      if (!selectedDiscountKey) return

      const discount = state.discount_list[selectedDiscountKey]
      if (!discount) return

      const termLength = state.termLength || 1
      const prepayLength = state.prepayLength || 1

      let shouldDeselect = false

      // Check TermLengthMin constraint
      if (
         discount.TermLengthMin != null &&
         termLength < discount.TermLengthMin
      ) {
         shouldDeselect = true
      }
      // Check TermLengthMax constraint
      if (
         discount.TermLengthMax != null &&
         termLength > discount.TermLengthMax
      ) {
         shouldDeselect = true
      }
      // Check PrepayLengthMin constraint
      if (
         discount.PrepayLengthMin != null &&
         prepayLength < discount.PrepayLengthMin
      ) {
         shouldDeselect = true
      }
      // Check PrepayLengthMax constraint
      if (
         discount.PrepayLengthMax != null &&
         prepayLength > discount.PrepayLengthMax
      ) {
         shouldDeselect = true
      }

      if (shouldDeselect) {
         // Auto-deselect by calling handleDiscountSelect with empty string
         state.setPromotionalDiscounts((prev) => {
            const newState = { ...prev }
            delete newState[discountFamily]
            delete newState[selectedDiscountKey]
            return newState
         })
         setSelectedDiscountKey(null)
      }
   }, [
      state.termLength,
      state.prepayLength,
      selectedDiscountKey,
      state.discount_list,
      discountFamily,
   ])

   const handleDiscountSelect = (discountKey: string) => {
      if (!discountKey) {
         // Deselect - remove from promotionalDiscounts but preserve numberDiscountValues
         state.setPromotionalDiscounts((prev) => {
            const newState = { ...prev }
            delete newState[discountFamily]
            // Remove all family members' boolean flags
            for (const dk of familyDiscounts) {
               delete newState[dk]
            }
            return newState
         })
         setSelectedDiscountKey(null)
         setIsOpen(false)
         return
      }

      const discount = state.discount_list[discountKey]
      const discountCode = discount?.DiscountCode

      if (selectedDiscountKey === discountKey) {
         // Deselect if clicking the same option - preserve numberDiscountValues
         state.setPromotionalDiscounts((prev) => {
            const newState = { ...prev }
            delete newState[discountFamily]
            // Remove all family members' boolean flags
            for (const dk of familyDiscounts) {
               delete newState[dk]
            }
            return newState
         })
         setSelectedDiscountKey(null)
      } else {
         // Mark this family as user-owned so pricing adjustments won't overwrite it
         state.recordUserSetFamilyKeys({ [discountFamily]: discountCode })
         // Select new discount
         state.setPromotionalDiscounts((prev) => {
            const newState = { ...prev }

            // Remove all other members of this family so only one can be active at a time
            for (const dk of familyDiscounts) {
               if (dk !== discountKey) {
                  delete newState[dk]
               }
            }

            // Set new selection
            newState[discountFamily] = discountCode

            // For boolean discounts, set to true immediately
            if (discount.InputType !== 'Number') {
               newState[discountKey] = true
            } else {
               // For number discounts, initialize numberDiscountValues if needed
               if (!newState.numberDiscountValues) {
                  newState.numberDiscountValues = {}
               }
               // Check if there's already a saved value for this discount
               const savedValue = newState.numberDiscountValues?.[discountKey]
               const hasSavedValue =
                  savedValue !== undefined &&
                  savedValue !== null &&
                  savedValue > 0
               // Set discount flag based on whether there's a saved value
               newState[discountKey] = hasSavedValue
            }

            return newState
         })
         setSelectedDiscountKey(discountKey)
      }
      setIsOpen(false)
   }

   const getDisplayText = () => {
      if (!selectedDiscountKey) {
         return `Select ${discountFamily} discount...`
      }

      const discount = state.discount_list[selectedDiscountKey]
      let formattedName = discount?.DiscountName || ''

      if (formattedName.includes('$PRODUCTNAME')) {
         if (discount?.ApplyTo?.split(',')?.length === 1) {
            formattedName = formattedName.replace(
               /\$PRODUCTNAME/g,
               state.product_list?.[discount.ApplyTo]?.ProductName || '',
            )
         } else {
            formattedName = removeDiscountNameVariables(formattedName)
         }
      }

      return formattedName
   }

   if (familyDiscounts.length === 0) {
      return null // Don't render if no discounts in family
   }

   const renderDiscountInput = () => {
      if (!selectedDiscountKey) return null

      const discount = state.discount_list[selectedDiscountKey]
      const isDisabled = shouldDisableDiscount(state, selectedDiscountKey)

      // For boolean discounts, show percentage (activation is handled by useEffect)
      if (discount.InputType !== 'Number') {
         return (
            <span className='discount-percentage'>
               {formatFloatAsPercent(discount.DiscountAmount)}
            </span>
         )
      }

      // For number discounts:
      // If disabled AND a value is already saved, render the read-only saved
      // value (preserves backward-compat for saved quotes opened by a user who
      // can no longer edit). Otherwise fall through and render the input —
      // disabled={isDisabled} grays it out but keeps the box visible, so users
      // can see *what* to enter (instead of a bare "[Input Required]" label).
      if (isDisabled) {
         const savedValue =
            state.promotionalDiscounts?.numberDiscountValues?.[
               selectedDiscountKey
            ]
         const hasSavedValue = savedValue !== undefined && savedValue !== null

         if (hasSavedValue) {
            // Activation for saved values is handled by useEffect
            return (
               <span className='discount-percentage'>
                  {discount.DiscountType === 'percent'
                     ? formatFloatAsPercent(savedValue)
                     : formatFloatAsCurrencySymbol(
                          savedValue,
                          state.currencyObject,
                       )}
               </span>
            )
         }

         // No saved value yet — fall through to render the input (disabled).
      }

      const maxDecimals = discount.DiscountType === 'percent' ? 5 : 2

      return (
         <input
            className='form-control'
            type='text'
            inputMode='decimal'
            disabled={isDisabled}
            value={displayValue}
            placeholder={
               discount.DiscountType === 'fixed' && state.currencyObject?.Code
                  ? state.currencyObject.Code
                  : '0'
            }
            onChange={(e) => {
               const raw = e.target.value

               // Only allow digits and one decimal point
               if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return

               // Enforce max decimal places while typing
               const dotIndex = raw.indexOf('.')
               if (dotIndex !== -1 && raw.length - dotIndex - 1 > maxDecimals)
                  return

               // Enforce max value for percent discounts while typing — clamp to max
               const parsedValue = parseFloat(raw)
               let numericValue = parsedValue
               let clampedRaw = raw
               if (discount.DiscountType === 'percent' && !isNaN(parsedValue)) {
                  const maxValue = discount.MaxDiscount || 100
                  if (parsedValue > maxValue) {
                     numericValue = maxValue
                     clampedRaw = String(maxValue)
                  }
               }

               // Update local display string (preserves trailing zeros and bare decimal point)
               setDisplayValue(clampedRaw)

               // Commit numeric value to global state
               const committedValue =
                  isNaN(numericValue) || raw === '' ? 0 : numericValue
               state.setPromotionalDiscounts((prev) => ({
                  ...prev,
                  [selectedDiscountKey]: committedValue > 0,
                  numberDiscountValues: {
                     ...prev['numberDiscountValues'],
                     [selectedDiscountKey]: committedValue,
                  },
               }))
            }}
            onBlur={(e) => {
               let inputValue = Number(e.target.value)

               // Enforce max value for percent discounts
               if (discount.DiscountType === 'percent') {
                  const maxValue = discount.MaxDiscount || 100
                  inputValue = Math.min(inputValue, maxValue)
               }

               // Round number to appropriate decimal places
               const roundedNumber = isNaN(inputValue)
                  ? 0
                  : discount.DiscountType === 'percent'
                    ? Number(inputValue.toFixed(5))
                    : Number(inputValue.toFixed(2))

               // Strip extraneous trailing zeros for display (e.g. 1.50 → "1.5", 1.00 → "1")
               setDisplayValue(roundedNumber > 0 ? String(roundedNumber) : '')

               // Update state with validated and formatted value
               state.setPromotionalDiscounts((prev) => ({
                  ...prev,
                  [selectedDiscountKey]: roundedNumber > 0,
                  numberDiscountValues: {
                     ...prev['numberDiscountValues'],
                     [selectedDiscountKey]: roundedNumber,
                  },
               }))
            }}
         />
      )
   }

   const getShortDisplayText = () => {
      if (!selectedDiscountKey) {
         return 'None'
      }

      const discount = state.discount_list[selectedDiscountKey]
      let formattedName = discount?.DiscountName || ''

      if (formattedName.includes('$PRODUCTNAME')) {
         if (discount?.ApplyTo?.split(',')?.length === 1) {
            formattedName = formattedName.replace(
               /\$PRODUCTNAME/g,
               state.product_list?.[discount.ApplyTo]?.ProductName || '',
            )
         } else {
            formattedName = removeDiscountNameVariables(formattedName)
         }
      }

      // Truncate long names for the button display
      if (formattedName.length > 12) {
         return formattedName.substring(0, 9) + '...'
      }

      return formattedName
   }

   return (
      <div className='discount-family-dropdown row' style={{ order }}>
         <div className='col-bhr1'>
            <div className='discount-family-label'>
               <span>{discountFamily}</span>
            </div>
         </div>

         <div className='col'>
            <div className='dropdown' ref={dropdownRef}>
               <button
                  className={`dropdown-toggle text-start discount-select-btn ${selectedDiscountKey ? 'btn-outline-primary' : ''}`}
                  type='button'
                  onClick={() => setIsOpen(!isOpen)}
                  aria-expanded={isOpen}
               >
                  {getShortDisplayText()}
               </button>

               {isOpen && (
                  <div className='dropdown-menu show discount-family-dropdown-menu'>
                     <div
                        className={`dropdown-item ${!selectedDiscountKey ? 'active' : ''}`}
                        onClick={() => handleDiscountSelect('')}
                     >
                        <em>None</em>
                     </div>
                     <div className='dropdown-divider'></div>
                     {familyDiscounts.map((discountKey) => {
                        const isOptionDisabled =
                           shouldDisableDiscountFamilyOption(state, discountKey)
                        return (
                           <DiscountFamilyDiscountOption
                              key={discountKey}
                              discountKey={discountKey}
                              isSelected={selectedDiscountKey === discountKey}
                              onSelect={handleDiscountSelect}
                              disabled={isOptionDisabled}
                              disabledReason={
                                 isOptionDisabled
                                    ? getDisabledReason(state, discountKey)
                                    : null
                              }
                           />
                        )
                     })}
                  </div>
               )}
            </div>
         </div>

         <div className='col'>
            {selectedDiscountKey ? renderDiscountInput() : '0%'}
         </div>
      </div>
   )
}

function DiscountFamilyDiscountOption({
   discountKey,
   isSelected,
   onSelect,
   disabled = false,
   disabledReason = null,
}: DiscountFamilyDiscountOptionProps) {
   const state = useQuoteContext()
   const discount = state.discount_list[discountKey]
   const [isHovered, setIsHovered] = useState(false)

   // Allow selecting disabled discounts - we'll handle the disabled state in the input
   const handleClick = () => {
      onSelect(discountKey)
   }

   if (!discount) return null

   // Format discount name with product substitution
   let formattedDiscountName = discount.DiscountName || ''
   if (formattedDiscountName.includes('$PRODUCTNAME')) {
      if (discount.ApplyTo?.split(',')?.length === 1) {
         formattedDiscountName = formattedDiscountName.replace(
            /\$PRODUCTNAME/g,
            state.product_list?.[discount.ApplyTo]?.ProductName || '',
         )
      } else {
         formattedDiscountName = removeDiscountNameVariables(
            formattedDiscountName,
         )
      }
   }

   const getDiscountValueDisplay = () => {
      if (discount.InputType === 'Number') {
         // Check if user has permissions for this discount
         const hasPermissions =
            !discount.RoleRequired ||
            discount.RoleRequired.split(',').includes(state.user_role) ||
            state.user_role === 'Super Admin'

         // If user has permissions, always show "Input Required"
         if (hasPermissions) {
            const currencySymbol =
               discount.DiscountType !== 'percent'
                  ? state.currencyObject?.Symbol + ' '
                  : ''
            return `${currencySymbol}[Input Required]`
         }

         // If user doesn't have permissions, show saved value or "Input Required"
         const storedValue =
            state.promotionalDiscounts?.numberDiscountValues?.[discountKey]
         const hasStoredValue =
            storedValue !== undefined && storedValue !== null

         if (hasStoredValue) {
            // Show the stored value if it exists
            return discount.DiscountType === 'percent'
               ? formatFloatAsPercent(storedValue)
               : formatFloatAsCurrencySymbol(storedValue, state.currencyObject)
         }

         // For number discounts without stored values, show the input type and currency if applicable
         const currencySymbol =
            discount.DiscountType !== 'percent'
               ? state.currencyObject?.Symbol + ' '
               : ''
         return `${currencySymbol}[Input Required]`
      } else {
         // For boolean discounts, show the fixed percentage
         return formatFloatAsPercent(discount.DiscountAmount)
      }
   }

   const tooltipText =
      disabled && disabledReason
         ? disabledReason
         : discount.DiscountDescription || ''

   return (
      <div
         className={`dropdown-item discount-option ${isSelected ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
         onClick={handleClick}
         onMouseEnter={() => setIsHovered(true)}
         onMouseLeave={() => setIsHovered(false)}
         title={disabled && disabledReason ? disabledReason : undefined}
      >
         <div className='d-flex justify-content-between align-items-start'>
            <div className='discount-info flex-grow-1'>
               <div className='discount-name'>{formattedDiscountName}</div>
               {tooltipText && isHovered && (
                  <div className='discount-description-tooltip'>
                     <small
                        className={`discount-description ${disabled && disabledReason ? 'text-warning' : 'text-muted'}`}
                     >
                        {tooltipText}
                     </small>
                  </div>
               )}
            </div>
            <div className='discount-value text-end'>
               <span className='badge bg-light text-dark'>
                  {getDiscountValueDisplay()}
               </span>
            </div>
         </div>
      </div>
   )
}
